import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Content, useContent } from 'src/contexts/ContentContext';
import { PosCurrencyField } from 'src/core/POS/PosCurrencyField';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { ToggleGroup } from 'src/core/ui/ToggleGroup';
import { useUndercutAmountSettings } from 'src/hooks/useUndercutAmountSettings';
import { IconsFill, MinusIcon, PlusIcon } from 'src/svgs/Viagogo';
import { getUndercutPriceRepresentingMode } from 'src/utils/autoPricingUtils';
import { UndercutPriceSettingsMode } from 'src/utils/autoPricingUtils';
import { ContentId } from 'src/utils/constants/contentId';
import { roundToPrecision } from 'src/utils/numberFormatter';
import { AutoPricingInputs, UiCurrency } from 'src/WebApiController';

import { usePriceViewDirection } from './CompListingPrices.hooks';
import * as styles from './PricingSetting.css';

const UNDERCUT_PRICE_MODE_OPTIONS = [
  {
    value: UndercutPriceSettingsMode.Premium,
    children: <PlusIcon fill={IconsFill.currentColor} size={vars.iconSize.m} />,
  },
  {
    value: UndercutPriceSettingsMode.Discount,
    children: (
      <MinusIcon fill={IconsFill.currentColor} size={vars.iconSize.m} />
    ),
  },
];

type CompListingPricesProps = {
  useVerticalView: boolean;
  uiCurrency: UiCurrency;
};

export const CompListingPrices = ({
  useVerticalView,
  uiCurrency,
}: CompListingPricesProps) => {
  const { ref, shouldUseVerticalView } = usePriceViewDirection();

  const forceVerticalView = useMemo(
    () => useVerticalView || shouldUseVerticalView,
    [shouldUseVerticalView, useVerticalView]
  );

  const { watch, setValue } = useFormContext<AutoPricingInputs>();
  const { undercutRelativeAmount, undercutAbsoluteAmount } = watch();

  const {
    undercutAbsoluteAmountMode,
    setUndercutAbsoluteAmountMode,
    undercutRelativeAmountMode,
    setUndercutRelativeAmountMode,
  } = useUndercutAmountSettings(undercutAbsoluteAmount, undercutRelativeAmount);
  const onSetUnderCutPrice = useCallback(
    (value: number | null, relative: boolean) => {
      if (relative) {
        setValue('undercutRelativeAmount', value);
      } else {
        setValue('undercutAbsoluteAmount', value);
      }
    },
    [setValue]
  );

  return (
    <>
      {!forceVerticalView && <Content id={ContentId.AdjustPrice} />}
      <Stack
        ref={ref}
        direction={forceVerticalView ? 'column' : 'row'}
        gap="m"
        width="full"
        justifyContent="spaceBetween"
        alignItems="center"
        style={!forceVerticalView ? { overflow: 'auto' } : undefined}
      >
        {forceVerticalView ? (
          <UnderCutPriceSettingsVerticalView
            uiCurrency={uiCurrency}
            undercutRelativeAmount={undercutRelativeAmount}
            undercutAbsoluteAmount={undercutAbsoluteAmount}
            undercutAbsoluteAmountMode={undercutAbsoluteAmountMode}
            undercutRelativeAmountMode={undercutRelativeAmountMode}
            setUndercutAbsoluteAmountMode={setUndercutAbsoluteAmountMode}
            setUndercutRelativeAmountMode={setUndercutRelativeAmountMode}
            onSetUnderCutPrice={onSetUnderCutPrice}
          />
        ) : (
          <UnderCutPriceSettingsHorizontalView
            uiCurrency={uiCurrency}
            undercutRelativeAmount={undercutRelativeAmount}
            undercutAbsoluteAmount={undercutAbsoluteAmount}
            undercutAbsoluteAmountMode={undercutAbsoluteAmountMode}
            undercutRelativeAmountMode={undercutRelativeAmountMode}
            setUndercutAbsoluteAmountMode={setUndercutAbsoluteAmountMode}
            setUndercutRelativeAmountMode={setUndercutRelativeAmountMode}
            onSetUnderCutPrice={onSetUnderCutPrice}
          />
        )}
      </Stack>
    </>
  );
};

type UnderCutPriceSettingsViewProps = {
  uiCurrency: UiCurrency;
  undercutRelativeAmount: number | null;
  undercutAbsoluteAmount: number | null;
  undercutAbsoluteAmountMode: UndercutPriceSettingsMode | undefined;
  setUndercutAbsoluteAmountMode: React.Dispatch<
    React.SetStateAction<UndercutPriceSettingsMode | undefined>
  >;
  undercutRelativeAmountMode: UndercutPriceSettingsMode | undefined;
  setUndercutRelativeAmountMode: React.Dispatch<
    React.SetStateAction<UndercutPriceSettingsMode | undefined>
  >;
  onSetUnderCutPrice: (value: number | null, relative: boolean) => void;
};

const UnderCutPriceSettingsHorizontalView = ({
  uiCurrency,
  undercutRelativeAmount,
  undercutAbsoluteAmount,
  undercutAbsoluteAmountMode,
  undercutRelativeAmountMode,
  setUndercutAbsoluteAmountMode,
  setUndercutRelativeAmountMode,
  onSetUnderCutPrice,
}: UnderCutPriceSettingsViewProps) => {
  return (
    <>
      <ToggleGroup
        options={UNDERCUT_PRICE_MODE_OPTIONS}
        value={undercutRelativeAmountMode}
        itemClassName={styles.toggleGroupItem}
        itemTransparent
        onValueChange={(value) => {
          if (!value) {
            return;
          }

          const undercutRelativeAmountModeNew =
            value as UndercutPriceSettingsMode;

          setUndercutRelativeAmountMode(undercutRelativeAmountModeNew);

          if (undercutRelativeAmount) {
            const absUndercutRelativeAmountNew = Math.abs(
              undercutRelativeAmount
            );

            const undercutRelativeAmountNew = getUndercutPriceRepresentingMode(
              absUndercutRelativeAmountNew,
              undercutRelativeAmountModeNew
            );

            onSetUnderCutPrice(undercutRelativeAmountNew, true);
          }
        }}
      />
      <div className={styles.inlinePricingInputText}>
        <Content id={ContentId.Price} />
      </div>
      <PosTextField
        type="number"
        className={styles.textAlignRight}
        rootProps={{
          className: styles.numberInput,
          variant: 'small',
        }}
        postfixDisplay="%"
        value={
          undercutRelativeAmount === null
            ? ''
            : roundToPrecision(Math.abs(undercutRelativeAmount) * 100, 8)
        }
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          const absUnderCutRelativeAmountNew =
            v >= 0 && v <= Number.MAX_VALUE ? Math.min(v, 100) / 100 : null;
          const underCutRelativeAmountNew =
            absUnderCutRelativeAmountNew != null
              ? getUndercutPriceRepresentingMode(
                  absUnderCutRelativeAmountNew,
                  undercutRelativeAmountMode
                )
              : null;

          onSetUnderCutPrice(underCutRelativeAmountNew, true);
        }}
      />
      <div className={styles.inlinePricingInputText}>
        <Content id={ContentId.Then} />
      </div>
      <ToggleGroup
        options={UNDERCUT_PRICE_MODE_OPTIONS}
        value={undercutAbsoluteAmountMode}
        itemClassName={styles.toggleGroupItem}
        itemTransparent
        onValueChange={(value) => {
          if (!value) {
            return;
          }

          const undercutAbsoluteAmountModeNew =
            value as UndercutPriceSettingsMode;

          setUndercutAbsoluteAmountMode(undercutAbsoluteAmountModeNew);

          if (undercutAbsoluteAmount) {
            const absUndercutAbsoluteAmountNew = Math.abs(
              undercutAbsoluteAmount
            );

            const underCutAbsoluteAmountNew = getUndercutPriceRepresentingMode(
              absUndercutAbsoluteAmountNew,
              undercutAbsoluteAmountModeNew
            );

            onSetUnderCutPrice(underCutAbsoluteAmountNew, false);
          }
        }}
      />
      <PosCurrencyField
        rootProps={{
          className: styles.numberInput,
          variant: 'small',
        }}
        className={styles.textAlignRight}
        uiCurrency={uiCurrency ?? undefined}
        value={
          undercutAbsoluteAmount == null ? '' : Math.abs(undercutAbsoluteAmount)
        }
        onChange={(e) => {
          const value = parseFloat(e.target.value);
          if (!isNaN(value)) {
            const undercutAbsoluteAmountNew = getUndercutPriceRepresentingMode(
              Math.abs(value),
              undercutAbsoluteAmountMode
            );

            onSetUnderCutPrice(undercutAbsoluteAmountNew, false);
          }
        }}
      />
    </>
  );
};

const UnderCutPriceSettingsVerticalView = ({
  uiCurrency,
  undercutRelativeAmount,
  undercutAbsoluteAmount,
  undercutAbsoluteAmountMode,
  undercutRelativeAmountMode,
  setUndercutAbsoluteAmountMode,
  setUndercutRelativeAmountMode,
  onSetUnderCutPrice,
}: UnderCutPriceSettingsViewProps) => {
  const determinPriceText = useContent(ContentId.DeterminePrice);
  const movePriceText = useContent(ContentId.MovePrice);
  const thenText = useContent(ContentId.Then);
  const adjustPriceText = useContent(ContentId.AdjustPrice);

  const compListingPriceTextFirst = useMemo(
    () => `${determinPriceText}: ${movePriceText}`,
    [determinPriceText, movePriceText]
  );

  const compListingPriceTextThen = useMemo(
    () => `${thenText} ${adjustPriceText.toLowerCase()}`,
    [adjustPriceText, thenText]
  );
  return (
    <>
      <div className={styles.compListingPriceInputText}>
        {compListingPriceTextFirst}
      </div>
      <Stack
        gap="m"
        justifyContent="spaceBetween"
        width="full"
        alignItems="center"
        style={{ overflow: 'auto' }}
      >
        <ToggleGroup
          options={UNDERCUT_PRICE_MODE_OPTIONS}
          value={undercutRelativeAmountMode}
          itemClassName={styles.toggleGroupItem}
          itemTransparent
          onValueChange={(value) => {
            if (!value) {
              return;
            }

            const undercutRelativeAmountModeNew =
              value as UndercutPriceSettingsMode;

            setUndercutRelativeAmountMode(undercutRelativeAmountModeNew);

            if (undercutRelativeAmount) {
              const absUndercutRelativeAmountNew = Math.abs(
                undercutRelativeAmount
              );

              const undercutRelativeAmountNew =
                getUndercutPriceRepresentingMode(
                  absUndercutRelativeAmountNew,
                  undercutRelativeAmountModeNew
                );

              onSetUnderCutPrice(undercutRelativeAmountNew, true);
            }
          }}
        />
        <div className={styles.inlinePricingInputText}>
          <Content id={ContentId.By} />
        </div>
        <PosTextField
          type="number"
          className={styles.textAlignRight}
          rootProps={{
            className: styles.numberInput,
            variant: 'small',
          }}
          postfixDisplay="%"
          value={
            undercutRelativeAmount === null
              ? ''
              : roundToPrecision(Math.abs(undercutRelativeAmount) * 100, 8)
          }
          onChange={(e) => {
            const v = parseFloat(e.target.value);
            const absUnderCutRelativeAmountNew =
              v >= 0 && v <= Number.MAX_VALUE ? Math.min(v, 100) / 100 : null;
            const underCutRelativeAmountNew =
              absUnderCutRelativeAmountNew != null
                ? getUndercutPriceRepresentingMode(
                    absUnderCutRelativeAmountNew,
                    undercutRelativeAmountMode
                  )
                : null;

            onSetUnderCutPrice(underCutRelativeAmountNew, true);
          }}
        />
      </Stack>
      <div className={styles.compListingPriceInputText}>
        {compListingPriceTextThen}
      </div>
      <Stack
        gap="m"
        justifyContent="spaceBetween"
        width="full"
        alignItems="center"
        style={{ overflow: 'auto' }}
      >
        <ToggleGroup
          options={UNDERCUT_PRICE_MODE_OPTIONS}
          value={undercutAbsoluteAmountMode}
          itemClassName={styles.toggleGroupItem}
          itemTransparent
          onValueChange={(value) => {
            if (!value) {
              return;
            }

            const undercutAbsoluteAmountModeNew =
              value as UndercutPriceSettingsMode;

            setUndercutAbsoluteAmountMode(undercutAbsoluteAmountModeNew);

            if (undercutAbsoluteAmount) {
              const absUndercutAbsoluteAmountNew = Math.abs(
                undercutAbsoluteAmount
              );

              const underCutAbsoluteAmountNew =
                getUndercutPriceRepresentingMode(
                  absUndercutAbsoluteAmountNew,
                  undercutAbsoluteAmountModeNew
                );

              onSetUnderCutPrice(underCutAbsoluteAmountNew, false);
            }
          }}
        />
        <div className={styles.inlinePricingInputText}>
          <Content id={ContentId.By} />
        </div>
        <PosCurrencyField
          rootProps={{
            className: styles.numberInput,
            variant: 'small',
          }}
          className={styles.textAlignRight}
          uiCurrency={uiCurrency ?? undefined}
          value={
            undercutAbsoluteAmount == null
              ? ''
              : Math.abs(undercutAbsoluteAmount)
          }
          onChange={(e) => {
            const value = parseFloat(e.target.value);
            if (!isNaN(value)) {
              const undercutAbsoluteAmountNew =
                getUndercutPriceRepresentingMode(
                  Math.abs(value),
                  undercutAbsoluteAmountMode
                );

              onSetUnderCutPrice(undercutAbsoluteAmountNew, false);
            }
          }}
        />
      </Stack>
    </>
  );
};
